import { useState, useEffect, useCallback } from 'react';
import './Toast.css';

const TOAST_EVENT = 'fokus:toast';

/**
 * Fire a global toast notification from anywhere (including non-React code).
 *
 * @param {string} message - Text to display
 * @param {Object} [options]
 * @param {'error'|'success'|'info'} [options.type='info'] - Visual style
 * @param {number} [options.duration=5000] - Auto-dismiss after ms (0 = persist until clicked)
 * @param {Function} [options.onClick] - Action to run when the toast is clicked (toast dismisses after)
 * @param {string} [options.actionHint] - Small hint line shown when the toast is actionable (e.g. "Click to open")
 * @param {string} [options.title] - Bold first line. Used by messenger-style toasts.
 */
export function showToast(message, { type = 'info', duration = 5000, onClick = null, actionHint = null, title = null } = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(TOAST_EVENT, { detail: { message, type, duration, onClick, actionHint, title } })
  );
}

let nextId = 1;

/**
 * Global toast container. Render once near the app root. Listens for
 * `fokus:toast` window events and stacks notifications in the corner.
 */
export default function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const { message, type = 'info', duration = 5000, onClick = null, actionHint = null, title = null } = e.detail || {};
      if (!message && !title) return;
      const id = nextId++;
      setToasts((prev) => [...prev, { id, message, type, onClick, actionHint, title }]);
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
    };
    window.addEventListener(TOAST_EVENT, handler);
    return () => window.removeEventListener(TOAST_EVENT, handler);
  }, [dismiss]);

  if (toasts.length === 0) return null;

  return (
    <div className="fokus-toast-container">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`fokus-toast fokus-toast--${t.type}${t.onClick ? ' fokus-toast--actionable' : ''}`}
          role="alert"
          onClick={() => {
            try {
              t.onClick?.();
            } finally {
              dismiss(t.id);
            }
          }}
        >
          {t.title && <span className="fokus-toast__title">{t.title}</span>}
          {t.message && <span className="fokus-toast__message">{t.message}</span>}
          {t.onClick && (
            <span className="fokus-toast__action-hint">{t.actionHint || 'Click to open'}</span>
          )}
        </div>
      ))}
    </div>
  );
}
